/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/DataInput/DataInput.tsx.
 */

import React, { useState } from "react";
import "../Contributor.css";
import "./DataInput.css";
import { createSubmission } from "../../../../services/submissionService";
import ConfirmModal from "../../../../components/common/ConfirmModal";
import Button from "../../../../components/common/Button";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import BulkImportModal, { BulkPreview } from "./components/BulkImportModal";
import DataCollectionGuideLink from "./components/DataCollectionGuideLink";
import DataCollectionTemplateLink from "./components/DataCollectionTemplateLink";

interface DataInputProps {
  onNext: (submissionId: string) => void;
  onBulkParsed?: (preview: BulkPreview) => void;
}

const DataInput: React.FC<DataInputProps> = ({ onNext, onBulkParsed }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleStartClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      // --- meaningful declaration ---
      const payload = {
        declaration: "I hereby declare that the data submitted is accurate.",
      };

      const res = await createSubmission(payload);

      if (res.data.success && res.data.data?.id) {
        const submissionId = res.data.data.id;
        setShowConfirmModal(false);
        onNext(submissionId); // 🔥 pass id upwards
      } else {
        console.error("Failed to create submission", res.data);
        setShowConfirmModal(false);
      }
    } catch (err: any) {
      console.error("Submission create failed:", err);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  return (
    <div className="data-input-page-container">
      <AnalysisBanner title="Material Data Input Process" />

      <div className="data-input-container">
        <div className="data-input-sections">
          <section className="data-input-card">
            <div className="data-input-card__title">Enter Data Manually</div>
            <div className="data-input-card__subtitle">
              Start a new submission and fill the forms in the portal.
            </div>

            <div className="data-input-card__footer">
              <DataCollectionGuideLink className="data-input-guide-link" />
              <div className="data-input-card__actions">
                <Button
                  label="Start new submission →"
                  onClick={handleStartClick}
                  disabled={loading}
                  type="button"
                  variant="default"
                />
              </div>
            </div>
          </section>

          <section className="data-input-card">
            <div className="data-input-card__title">Import Data</div>
            <div className="data-input-card__subtitle">
              Upload Excel to preview & import submissions in bulk.
            </div>

            <div className="data-input-import-embed">
              <div className="data-input-card__footer">
                <div className="data-input-resource-links">
                  <DataCollectionGuideLink className="data-input-guide-link" />
                  <DataCollectionTemplateLink className="data-input-guide-link" />
                </div>
                <div className="data-input-card__actions">
                  <Button
                    label="Import from Excel →"
                    onClick={() => setShowImportModal(true)}
                    type="button"
                    variant="default"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          onParsed={(preview) => {
            onBulkParsed?.(preview);
            setShowImportModal(false);
          }}
        />

        {/* Confirmation Modal */}
        <ConfirmModal
          open={showConfirmModal}
          title="Start Data Input Process"
          description="By proceeding, you are confirming that the information you provide will be truthful and accurate to the best of your knowledge. Do you want to start the data input process?"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          confirmText="Yes, Start"
          cancelText="Cancel"
          loading={loading}
          footerStart={
            <DataCollectionGuideLink className="data-input-guide-link data-input-guide-link--text" />
          }
        />
      </div>
    </div>
  );
};

/** React component — Data Input. */
export default DataInput;
